import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { DateRangeFilter, DateRangeTypeEnum, FilterItemList, PageTypeEnum, SelectedFilter } from '../shared/models/filter.model';
import { combineLatest, filter, map, pipe, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { ManipulationsToolbarService } from '../shared/services/manipulations-toolbar-service';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlobalStore } from './global-store';
import { EApprovalStatus, EnumApprovalsTabs } from '../models/approval.model';
import { ApprovalApiService } from '../services/approvals-api.service';
import { User } from '../models/user.model';

export interface ManipulationToolbarState {
    isDisplayNotActiveItems: boolean;
    pageType?: PageTypeEnum;
    filterList: FilterItemList[];
    dateRangeFilter: DateRangeFilter[];
    selectedFilter: SelectedFilter | null;
    isListView: boolean;
    isFilterListOpen: boolean;
    page: number;
    // Sender-name ("שם השולח") filter options for the Approvals/Reports pages - scoped to the
    // employees who actually submitted a request visible in the current page (and, for Approvals,
    // the current tab), unlike GlobalStore.names$ which lists all questionnaire creators/admins
    // system-wide.
    senderNames: User[];
    // Approver-name ("מאשרים") filter options for the Reports page - every admin that appears
    // anywhere in the approval chain of a report row, used by the unified multi-select approvers filter.
    reportApproverNames: User[];
    // "חודש שכר" filter options for the After Approval tab - payroll months (yyyyMM, newest first).
    payrollMonths: number[];
}

interface SenderNamesTrigger {
    pageType?: PageTypeEnum;
    approvalsTabs?: EnumApprovalsTabs;
}

@Injectable({ providedIn: 'root' })
//the reminders and groups to providers from themself
export class ManipulationsToolbarStore extends ComponentStore<ManipulationToolbarState> {
    #manipulationsToolbarService = inject(ManipulationsToolbarService);
    #globalStore = inject(GlobalStore);
    #approvalApiService = inject(ApprovalApiService);
    isDisplayNotActiveItems$ = this.select(x => x.isDisplayNotActiveItems);
    readonly senderNames$ = this.select((x) => x.senderNames);
    readonly reportApproverNames$ = this.select((x) => x.reportApproverNames);
    readonly payrollMonths$ = this.select((x) => x.payrollMonths);
    readonly isAdmin$ = this.#globalStore.isRoleAdmin$;
    readonly isApproverManager$ = this.#globalStore.isApproverManager$;
    readonly isAdminSig$ = toSignal(this.#globalStore.isRoleAdmin$);
    readonly filter$ = this.select((x) => x.filterList);
    readonly dateRangeFilter$ = this.select((x) => x.dateRangeFilter);
    readonly selectedFilter$ = this.select((x) => x.selectedFilter);
    readonly pageType$ = this.select((x) => x.pageType);
    readonly pageTypeSig$ = toSignal(this.pageType$);
    readonly page$ = this.select((x) => x.page);
    readonly isListView$ = this.select((x) => x.isListView);
    readonly isMyQuestionnaires$ = this.select((x) => x.selectedFilter?.myQuestionnaires);
    readonly approvalsTabs$ = this.select((x) => x.selectedFilter?.approvalsTabs);
    readonly pendingApprovalTab$ = this.select((x) => x.selectedFilter?.approvalsTabs == EnumApprovalsTabs.PendingApproval);
    readonly afterApprovalTab$ = this.select((x) => x.selectedFilter?.approvalsTabs == EnumApprovalsTabs.AfterApproval);
    readonly downloadRecordsTab$ = this.select((x) => x.selectedFilter?.approvalsTabs == EnumApprovalsTabs.DownloadRecords);
    readonly reportsTab$ = this.select((x) => x.selectedFilter?.approvalsTabs == EnumApprovalsTabs.Reports);
    readonly isDisplayUser$ = this.select((x) => x.selectedFilter?.myQuestionnaires || x.selectedFilter?.generalQuestionnaires || !this.isAdminSig$());
    readonly isGeneralQuestionnaires$ = this.select((x) => x.selectedFilter?.generalQuestionnaires);
    readonly isPendingApproval$ = this.select((x) => x.selectedFilter?.statusApproval?.includes(EApprovalStatus.PendingApproval));
    readonly isFilterListOpen$ = this.select((x) => x.isFilterListOpen);
    readonly pageSig$ = toSignal(this.page$);
    readonly filterList$ = combineLatest(this.filter$, this.#globalStore.allCategories$.pipe(map(x => x.length > 0)), this.isDisplayUser$, this.pageType$, this.isPendingApproval$, this.isApproverManager$)
        .pipe(
            map(([x, isExistsCategory, isDisplayUser, pageType, isPendingApproval, isApproverManager]) =>
                x.filter(x => {
                    if (x.fieldName == "categoryId" && !isExistsCategory)
                        return false;
                    if (x.fieldName == "status" && isDisplayUser)
                        return false;
                    if (x.fieldName == "statusToUser" && !isDisplayUser)
                        return false;
                    if (x.fieldName == "statusApproval" && pageType == PageTypeEnum.Approvals)
                        return false;
                    if (x.fieldName == "approvedDate" && isApproverManager && isPendingApproval)
                        return false;
                    return true;
                }
                )))

    constructor() {

        super({
            isDisplayNotActiveItems: false,
            filterList: [],
            selectedFilter: <SelectedFilter>{},
            dateRangeFilter: [],
            page: 1,
            isListView: true,
            isFilterListOpen: false,
            senderNames: [],
            reportApproverNames: [],
            payrollMonths: [],
        });
        // Both the filter list and the sender/approver-name options depend on the tab, not just the
        // page, now that Reports is a tab inside Approvals (rather than its own PageTypeEnum) - so
        // every one of these triggers keys off the (pageType, approvalsTabs) pair.
        // { debounce: true } waits for synchronous state updates to settle before emitting, so e.g.
        // ApprovalsComponent.ngOnInit's two separate patchState calls (pageType, then selectedFilter)
        // produce one emission with the final values instead of combineLatest's usual glitch of
        // one partial + one final emission - which was firing these effects twice per page load.
        const pageAndTab$ = this.select(
            this.pageType$,
            this.approvalsTabs$,
            (pageType, approvalsTabs): SenderNamesTrigger => ({ pageType, approvalsTabs }),
            {
                debounce: true,
                equal: (a: SenderNamesTrigger, b: SenderNamesTrigger) => a.pageType === b.pageType && a.approvalsTabs === b.approvalsTabs
            }
        );

        pageAndTab$
            .pipe(
                filter(({ pageType }) => pageType !== undefined),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.loadFilterList());

        this.select((x) => x.selectedFilter).subscribe(() => {
            this.#globalStore.updateIsOpenStatusFlow(false);
        });

        // Re-fetch the sender-name options whenever we're on the Approvals page: PendingApproval /
        // AfterApproval / DownloadRecords / Reports each scope to a different set of approvals, so
        // the list of relevant senders differs per tab.
        pageAndTab$
            .pipe(
                filter(({ pageType }) => pageType === PageTypeEnum.Approvals),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.loadSenderNames());

        // Approver-name ("מאשרים") filter options only exist on the Approvals page's Reports tab.
        pageAndTab$
            .pipe(
                filter(({ pageType, approvalsTabs }) => pageType === PageTypeEnum.Approvals && approvalsTabs === EnumApprovalsTabs.Reports),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.loadReportApproverNames());

        // Payroll-month ("חודש שכר") filter options only exist on the Approvals page's After Approval tab.
        pageAndTab$
            .pipe(
                filter(({ pageType, approvalsTabs }) => pageType === PageTypeEnum.Approvals && approvalsTabs === EnumApprovalsTabs.AfterApproval),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.loadPayrollMonths());

        this.loadByDefaultDateRange();
    }


    readonly updateIsDisplayNotActiveItems = this.updater((state, isActive: boolean) => ({
        ...state,
        page: 1,
        isDisplayNotActiveItems: isActive
    }));

    readonly updatePageType = this.updater((state, pageType: PageTypeEnum) => ({
        ...state,
        pageType: pageType
    }));
    readonly changeOpenFilterList = this.updater((state) => ({
        ...state,
        isFilterListOpen: !state.isFilterListOpen
    }));
    readonly updateViewState = this.updater((state, isListView: boolean) => ({
        ...state,
        isListView
    }));

    readonly loadSenderNames = this.effect<void>(
        pipe(
            withLatestFrom(this.select((x) => x.selectedFilter), this.pageType$, this.approvalsTabs$),
            switchMap(([_, selectedFilter, pageType, approvalsTabs]) =>
                pageType === PageTypeEnum.Approvals && approvalsTabs === EnumApprovalsTabs.Reports
                    ? this.#approvalApiService.getReportsSenderNamesList(selectedFilter)
                    : this.#approvalApiService.getSenderNamesList(selectedFilter)
            ),
            tap((senderNames: User[]) => this.patchState({ senderNames }))
        )
    );

    readonly loadReportApproverNames = this.effect<void>(
        pipe(
            withLatestFrom(this.select((x) => x.selectedFilter)),
            switchMap(([_, selectedFilter]) => this.#approvalApiService.getApproverNamesList(selectedFilter)),
            tap((reportApproverNames: User[]) => this.patchState({ reportApproverNames }))
        )
    );

    readonly loadPayrollMonths = this.effect<void>(
        pipe(
            switchMap(() => this.#approvalApiService.getPayrollMonthsList()),
            tap((payrollMonths: number[]) => this.patchState({ payrollMonths }))
        )
    );

    readonly loadByDefaultDateRange = this.effect<void>(
        pipe(
            switchMap((_) => this.#manipulationsToolbarService.getDateRangeFilter()),

            tap((dateRangeFilter: DateRangeFilter[]) => {
                this.patchState({ dateRangeFilter });
            }
            )
        )
    );

    readonly updateFilterDataSelected = this.updater(
        (state, data: Partial<SelectedFilter>) => {

            let selectedFilter: SelectedFilter = state.selectedFilter || <SelectedFilter>{};
            if (data.status && data.status.length == 0)
                this.removeFilter(['status'])

            else if (data.creator && data.creator.length == 0)
                this.removeFilter(['creator'])

            const updateSelectedFilter = { ...selectedFilter, ...data }
            return {
                ...state,
                page: 1,
                selectedFilter: updateSelectedFilter,

            };
        }
    );

    readonly removeFilter = this.updater((state, data: string[]) => {
        const existingSelectedFilter = state.selectedFilter;
        if (!existingSelectedFilter) {
            return state;
        }
        const updatedSelectedFilter = Object.keys(existingSelectedFilter)
            .filter(key => !data.includes(key)) // Exclude keys in data
            .reduce(
                (filteredObj: any, key: string) => {
                    if (filteredObj) {
                        filteredObj[key] = (existingSelectedFilter! as any)[key];
                        return filteredObj;
                    }
                },
                {} as SelectedFilter
            );
        return {
            ...state,
            page: 1,
            selectedFilter: updatedSelectedFilter
        };
    });

    readonly loadFilterList = this.effect<void>(
        pipe(
            withLatestFrom(this.select((x) => x.pageType), this.approvalsTabs$),
            switchMap(([_, pageType, approvalsTabs]) => this.#manipulationsToolbarService.getFilterItemList(pageType!, approvalsTabs)
            ),
            tap((filterList: FilterItemList[]) => this.patchState({ filterList }))
        )
    );
    readonly resetPage = this.updater(
        (state) => {
            return {
                ...state,
                page: 1
            }
        })

    readonly resetAllFilters = this.updater(
        (state) => {
            return {
                ...state,
                page: 1,
                selectedFilter: {},
                isDisplayNotActiveItems: false
            }
        })

    readonly resetFilters = this.updater(
        (state) => {
            const filterByNameExist = state.selectedFilter?.filterByName;
            const filterMyQuestionnariesExist = state.selectedFilter?.myQuestionnaires;
            const filterGeneralQuestionnairesExist = state.selectedFilter?.generalQuestionnaires;
            return {
                ...state,
                page: 1,
                selectedFilter: { filterByName: filterByNameExist, myQuestionnaires: filterMyQuestionnariesExist, generalQuestionnaires: filterGeneralQuestionnairesExist }
            }
        })

    // Same reset as resetFilters, but sets the new tab in the same patchState call instead of a
    // separate updateFilterDataSelected() call right after - two separate calls each emit their own
    // selectedFilter$ value, which fired getApprovals/getExtraApprovals twice per tab switch.
    readonly switchApprovalsTab = this.updater(
        (state, approvalsTab: EnumApprovalsTabs) => {
            const filterByNameExist = state.selectedFilter?.filterByName;
            const filterMyQuestionnariesExist = state.selectedFilter?.myQuestionnaires;
            const filterGeneralQuestionnairesExist = state.selectedFilter?.generalQuestionnaires;
            const selectedFilter: SelectedFilter = {
                filterByName: filterByNameExist,
                myQuestionnaires: filterMyQuestionnariesExist,
                generalQuestionnaires: filterGeneralQuestionnairesExist,
                approvalsTabs: approvalsTab
            };
            // Reports tab default view (DBJR-193): last 60 days, seeded the moment the tab is
            // opened - mirrors what a fresh ReportsComponent.ngOnInit used to do when Reports was
            // its own routed page.
            if (approvalsTab === EnumApprovalsTabs.Reports) {
                const today = new Date();
                const from = new Date();
                from.setDate(today.getDate() - 60);
                selectedFilter.createDate = {
                    dateRangeType: DateRangeTypeEnum.Custom,
                    customDateFromNTZ: from,
                    customDateToNTZ: today,
                };
            }
            return {
                ...state,
                page: 1,
                selectedFilter
            }
        })

    readonly resetFiltersWithoutTabs = this.updater(
        (state) => {
            const filterMyQuestionnariesExist = state.selectedFilter?.myQuestionnaires;
            const filterGeneralQuestionnairesExist = state.selectedFilter?.generalQuestionnaires;
            return {
                ...state,
                page: 1,
                selectedFilter: { myQuestionnaires: filterMyQuestionnariesExist, generalQuestionnaires: filterGeneralQuestionnairesExist }
            }
        })

    readonly resetFiltersWithoutStatusApproval = this.updater(
        (state) => {
            const status = state.selectedFilter?.statusApproval;
            return {
                ...state,
                page: 1,
                selectedFilter: { statusApproval: status }
            }
        })

    readonly updatePage = this.updater(
        (state) => {
            var currentPage: number = state.page;
            return {
                ...state,
                page: currentPage + 1
            }
        })
}